/**
 * Shared types, interfaces, and predicate matcher helper functions for table column filtering.
 * Issue #1839: shared column-header filters for Project Selector and Compliance tables.
 */

export type FilterType = 'text' | 'categorical' | 'date-range' | 'number-range';

export interface TextFilterCriterion {
  type: 'text';
  query: string;
}

export interface CategoricalFilterCriterion {
  type: 'categorical';
  values: string[];
}

export interface DateRangeFilterCriterion {
  type: 'date-range';
  start?: string | null; // ISO date string 'YYYY-MM-DD'
  end?: string | null; // ISO date string 'YYYY-MM-DD'
}

export type NumberTarget = 'count' | 'denominator';

export interface NumberRangeFilterCriterion {
  type: 'number-range';
  min?: number | null;
  max?: number | null;
  target?: NumberTarget;
}

export type ColumnFilterCriterion =
  | TextFilterCriterion
  | CategoricalFilterCriterion
  | DateRangeFilterCriterion
  | NumberRangeFilterCriterion;

export type TableFilterState = Record<string, ColumnFilterCriterion>;

export interface CategoricalOption {
  value: string;
  label: string;
  chipClass?: string;
}

export interface NumberTargetOption {
  value: NumberTarget;
  label: string;
}

export interface ColumnFilterConfig {
  columnId: string;
  columnLabel: string;
  type: FilterType;
  options?: (string | CategoricalOption)[];
  targetOptions?: NumberTargetOption[];
  defaultTarget?: NumberTarget;
  targetLabel?: string;
  placeholder?: string;
}

/** Returns true when a criterion contains non-empty active filter rules. */
export function isCriterionActive(criterion?: ColumnFilterCriterion | null): boolean {
  if (!criterion) return false;
  switch (criterion.type) {
    case 'text':
      return typeof criterion.query === 'string' && criterion.query.trim().length > 0;
    case 'categorical':
      return Array.isArray(criterion.values) && criterion.values.length > 0;
    case 'date-range':
      return Boolean((criterion.start && criterion.start.trim()) || (criterion.end && criterion.end.trim()));
    case 'number-range':
      return (
        (criterion.min !== undefined && criterion.min !== null) ||
        (criterion.max !== undefined && criterion.max !== null)
      );
    default:
      return false;
  }
}

/** Counts active column criteria in a filter state map. */
export function getActiveFilterCount(state: TableFilterState | null | undefined): number {
  if (!state) return 0;
  let count = 0;
  for (const key of Object.keys(state)) {
    if (isCriterionActive(state[key])) {
      count++;
    }
  }
  return count;
}

/** Evaluates text match with case-insensitivity. Empty query imposes no restriction. */
export function matchesTextCriterion(
  value: string | null | undefined,
  criterion?: TextFilterCriterion | null
): boolean {
  if (!criterion || !criterion.query || !criterion.query.trim()) {
    return true;
  }
  if (value === null || value === undefined) {
    return false;
  }
  return value.toLowerCase().includes(criterion.query.trim().toLowerCase());
}

/**
 * Evaluates categorical match with OR semantics across multiple selected values.
 * Empty selection imposes no restriction.
 */
export function matchesCategoricalCriterion(
  value: string | null | undefined,
  criterion?: CategoricalFilterCriterion | null
): boolean {
  if (!criterion || !criterion.values || criterion.values.length === 0) {
    return true;
  }
  if (value === null || value === undefined) {
    return false;
  }
  return criterion.values.includes(value);
}

/**
 * Evaluates date within [start, end] inclusive range.
 * Empty range imposes no restriction. Missing/unknown date does not match an active range.
 */
export function matchesDateRangeCriterion(
  dateValue: string | Date | null | undefined,
  criterion?: DateRangeFilterCriterion | null
): boolean {
  const hasStart = Boolean(criterion?.start && criterion.start.trim());
  const hasEnd = Boolean(criterion?.end && criterion.end.trim());

  if (!criterion || (!hasStart && !hasEnd)) {
    return true;
  }
  if (!dateValue) {
    return false;
  }

  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  if (isNaN(date.getTime())) {
    return false;
  }

  if (hasStart && criterion.start) {
    const parts = criterion.start.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      const startBoundary = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
      if (date < startBoundary) {
        return false;
      }
    }
  }

  if (hasEnd && criterion.end) {
    const parts = criterion.end.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      const endBoundary = new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
      if (date > endBoundary) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Evaluates numeric value within [min, max] range.
 * Empty criteria impose no restriction.
 * Distinguishes missing/unknown value from known zero: an unknown value is never treated as 0.
 */
export function matchesNumberRangeCriterion(
  numValue: number | null | undefined,
  criterion?: NumberRangeFilterCriterion | null,
  isKnown = true
): boolean {
  const hasMin = criterion?.min !== undefined && criterion?.min !== null;
  const hasMax = criterion?.max !== undefined && criterion?.max !== null;

  if (!criterion || (!hasMin && !hasMax)) {
    return true;
  }

  // Missing or unknown values never match an active numeric filter.
  if (!isKnown || numValue === null || numValue === undefined || isNaN(numValue)) {
    return false;
  }

  if (hasMin && numValue < (criterion.min as number)) {
    return false;
  }
  if (hasMax && numValue > (criterion.max as number)) {
    return false;
  }

  return true;
}
